'use client'

// The quiet in-app-browser NUDGE — one line, no box, no alarm.
//
// Sign-in is expected to work inside a webview now (implicit flow, OTP code),
// so this is not a warning and must never read like one: it is a whisper that
// the person's own browser is the smoother seat, with a one-tap attempt to get
// there. It never blocks anything, and it is distinct from InAppBrowserNotice,
// which appears only AFTER a sign-in actually came back empty.
//
// Rendered as plain prose in the ink ladder with one quiet underlined action —
// the house `.quiet-link` language, not a banner.

import { useEffect, useState } from 'react'
import { detectInAppBrowser, escapeToBrowser, type InAppInfo } from '../lib/inAppBrowser'
import { carryAttribution } from '../lib/utm'

export default function InAppHint() {
  const [info, setInfo] = useState<InAppInfo | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    // browser-only inputs (navigator.userAgent) — meaningless during SSR, so
    // the hint appears post-mount or not at all. It is a whisper; a flash-in
    // would defeat it, hence no entrance animation either.
    const i = detectInAppBrowser()
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (i.isInApp) setInfo(i)
  }, [])

  if (!info) return null

  const browser = info.platform === 'ios' ? 'safari' : 'chrome'

  const out = async () => {
    // the ad's tags and X's click id go with them (lib/utm carryAttribution)
    const url = carryAttribution(window.location.href)
    const attempted = escapeToBrowser(url)
    // the escape is best-effort (iOS especially) — put the link on the
    // clipboard too, so "nothing happened" still leaves them holding the key.
    if (!attempted || info.platform === 'ios') {
      try {
        await navigator.clipboard.writeText(url)
        setCopied(true)
        setTimeout(() => setCopied(false), 2200)
      } catch {
        /* clipboard blocked — the attempt above may still have worked */
      }
    }
  }

  return (
    <p
      style={{
        margin: 0,
        fontSize: '0.82rem',
        lineHeight: 1.55,
        color: 'var(--ink-3)',
        textWrap: 'pretty',
      }}
    >
      you&rsquo;re in {info.app ?? 'an app'}&rsquo;s built-in browser — sign-in works here, and it&rsquo;s
      smoother in your own.{' '}
      <button
        type="button"
        onClick={out}
        className="quiet-link"
        style={{
          appearance: 'none',
          background: 'none',
          border: 'none',
          padding: 0,
          font: 'inherit',
          color: 'var(--ink-2)',
          textDecoration: 'underline',
          textUnderlineOffset: '4px',
          cursor: 'pointer',
        }}
      >
        {copied ? 'link copied ✓' : `open in ${browser}`}
      </button>
    </p>
  )
}
